// Contracts register (P3.5; 01-SPEC §6 "register with renewal alerts at 90/60/30 days; one click
// creates a renewal PR", D56). Pure rules: which alert is due, the notice date, and the summary.

import { z } from 'zod';

export const CONTRACT_NO = /^(SMC|VIB|MED)-CT-\d{4,}-\d{4}$/;
export const BILLING_CYCLES = [
  'monthly',
  'quarterly',
  'annualUpfront',
  'milestones',
  'other',
] as const;
export type BillingCycle = (typeof BILLING_CYCLES)[number];
export const CONTRACT_CURRENCIES = ['SAR', 'AED', 'EGP', 'USD', 'EUR'] as const;
export const CONTRACT_STATUSES = ['active', 'renewing', 'ended', 'cancelled'] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];
/** Alerts before the end date, largest first (01-SPEC). */
export const ALERT_DAYS = [90, 60, 30] as const;

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .regex(/^[^<>]*$/, 'Remove < and >');

export const contractInputSchema = z
  .strictObject({
    name: text(150).min(1, 'Required'),
    supplierId: z.string().regex(/^SUP-[2-9A-HJ-NP-Z]{8}$/, 'Pick a supplier'),
    entity: z.enum(['SMC', 'VIB', 'MED']),
    department: text(100).min(1, 'Required'),
    owner: z.email('Use the owner’s work email').max(200),
    start: z.string().regex(DATE, 'Pick a date'),
    end: z.string().regex(DATE, 'Pick a date'),
    /** Annual value excluding VAT, major units. */
    value: z.number().nonnegative().max(1_000_000_000),
    currency: z.enum(CONTRACT_CURRENCIES),
    billingCycle: z.enum(BILLING_CYCLES),
    renewalNoticeDays: z.number().int().min(0).max(365).default(30),
    autoRenew: z.boolean().default(false),
    poNo: z.union([z.literal(''), z.string().regex(/^[A-Z]{3}-PO-\d{4,}-\d{4}$/)]).default(''),
    notes: text(1000).default(''),
  })
  .refine((c) => c.end > c.start, {
    message: 'The end date must be after the start',
    path: ['end'],
  });
export type ContractInput = z.infer<typeof contractInputSchema>;

export const contractUpdateSchema = z.strictObject({
  etag: z.string().min(1).max(200),
  end: z.string().regex(DATE, 'Pick a date').optional(),
  value: z.number().nonnegative().max(1_000_000_000).optional(),
  renewalNoticeDays: z.number().int().min(0).max(365).optional(),
  autoRenew: z.boolean().optional(),
  owner: z.email().max(200).optional(),
  status: z.enum(CONTRACT_STATUSES).optional(),
  notes: text(1000).optional(),
});

const DAY = 86_400_000;
const at = (date: string) => new Date(`${date}T00:00:00Z`).getTime();

/** Whole days from `today` to `date` (both YYYY-MM-DD); negative once past. */
export const daysUntil = (date: string, today: string) => Math.round((at(date) - at(today)) / DAY);

/** The last day to give notice: end − notice days (YYYY-MM-DD). */
export function noticeBy(end: string, noticeDays: number): string {
  return new Date(at(end) - noticeDays * DAY).toISOString().slice(0, 10);
}

/**
 * The alert to send now: the smallest threshold reached (90, 60 or 30 days before the end) that
 * has not been sent for this end date. A contract first seen at 20 days left gets one alert (30),
 * never three. `sent` holds keys like "60:2026-12-31".
 */
export function alertDue(
  end: string,
  today: string,
  sent: ReadonlySet<string>,
): (typeof ALERT_DAYS)[number] | null {
  const left = daysUntil(end, today);
  if (left < 0) return null;
  const reached = ALERT_DAYS.filter((d) => left <= d);
  const threshold = reached[reached.length - 1];
  if (threshold === undefined || sent.has(alertKey(threshold, end))) return null;
  return threshold;
}

export const alertKey = (days: number, end: string) => `${days}:${end}`;

export function sentKeys(stored: string): Set<string> {
  return new Set(
    stored
      .split(';')
      .map((s) => s.trim())
      .filter(Boolean),
  );
}

export interface ContractView {
  contractNo: string;
  etag: string;
  name: string;
  supplierId: string;
  supplierName: string;
  entity: string;
  department: string;
  owner: string;
  start: string;
  end: string;
  /** Annual value excl. VAT, minor units. */
  value: number;
  currency: string;
  billingCycle: BillingCycle;
  renewalNoticeDays: number;
  noticeBy: string;
  daysToEnd: number;
  daysToNotice: number;
  autoRenew: boolean;
  status: ContractStatus;
  poNo: string;
  renewalPrNo: string;
  notes: string;
}

export interface ContractsBody {
  today: string;
  contracts: ContractView[];
  summary: {
    active: number;
    renewing90: number;
    autoRenew: number;
    /** Annual value of active contracts per currency, minor units. */
    committed: Record<string, number>;
  };
}

/** Tiles: active contracts, ending within 90 days, auto-renewing, annual value per currency. */
export function summarise(contracts: ContractView[]): ContractsBody['summary'] {
  const live = contracts.filter((c) => c.status === 'active' || c.status === 'renewing');
  const committed: Record<string, number> = {};
  for (const c of live) committed[c.currency] = (committed[c.currency] ?? 0) + c.value;
  return {
    active: live.length,
    renewing90: live.filter((c) => c.daysToEnd >= 0 && c.daysToEnd <= 90).length,
    autoRenew: live.filter((c) => c.autoRenew).length,
    committed,
  };
}

/** What a renewal request starts with (the requester completes and submits it). */
export interface RenewalDraft {
  contractNo: string;
  title: string;
  justification: string;
  lines: {
    description: string;
    qty: number;
    unit: 'month' | 'lot';
    lineType: 'recurring' | 'service';
  }[];
  estBudget: number | null;
}

export function renewalDraft(c: ContractView): RenewalDraft {
  const monthly = c.billingCycle === 'monthly';
  return {
    contractNo: c.contractNo,
    title: `Renewal: ${c.name}`.slice(0, 200),
    justification: `Renewal of contract ${c.contractNo} with ${c.supplierName}, ending ${c.end}.${c.poNo ? ` Previous PO ${c.poNo}.` : ''}`,
    lines: [
      {
        description: c.name,
        qty: monthly ? 12 : 1,
        unit: monthly ? 'month' : 'lot',
        lineType: monthly ? 'recurring' : 'service',
      },
    ],
    estBudget: c.value > 0 ? c.value / 100 : null,
  };
}
